import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  TextField,
  Button,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { api } from "../../utils/api";

export const emptyFilters = {
  search: "",
  status: [],
  source: [],
  stage: [],
  client_id: "",
  manager_id: "",
  assigned_to: "",
  date_from: "",
  date_to: "",
  price_min: "",
  price_max: "",
  overdue: "",
  sort_by: "id",
  sort_order: "desc",
};

const SORT_OPTIONS = [
  { value: "id", label: "Job ID" },
  { value: "created_at", label: "Created Date" },
  { value: "client_name", label: "Client Name" },
  { value: "total_price", label: "Total Price" },
  { value: "status", label: "Status" },
];

export default function JobFilterDrawer({ open, onClose, filters, onApply }) {
  const [local, setLocal] = useState(filters || emptyFilters);
  const [opts, setOpts] = useState({
    statuses: [],
    sources: [],
    stages: [],
    clients: [],
    managers: [],
    assignees: [],
    price: { min: 0, max: 0 },
  });

  useEffect(() => {
    setLocal(filters || emptyFilters);
  }, [filters, open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/jobs/filter_options.php");
        setOpts({
          statuses: res.statuses || [],
          sources: res.sources || [],
          stages: res.stages || [],
          clients: res.clients || [],
          managers: res.managers || [],
          assignees: res.assignees || [],
          price: res.price || { min: 0, max: 0 },
        });
      } catch (e) {
        console.error("Filter options load failed", e);
      }
    })();
  }, [open]);

  const set = (key, value) => setLocal((f) => ({ ...f, [key]: value }));

  const handleApply = () => {
    onApply(local);
    onClose();
  };

  const handleReset = () => {
    setLocal(emptyFilters);
  };

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 1 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, p: 2.5, display: "flex", flexDirection: "column", height: "100%" }}>
        {/* HEADER */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Filter Jobs
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
          {/* BASIC */}
          <Section title="Basic">
            <TextField
              fullWidth
              size="small"
              label="Search (client / job id / remark)"
              value={local.search}
              onChange={(e) => set("search", e.target.value)}
              sx={{ mb: 1.5 }}
            />

            <Autocomplete
              multiple
              size="small"
              options={opts.statuses}
              value={local.status}
              onChange={(_, v) => set("status", v)}
              renderInput={(p) => <TextField {...p} label="Status" />}
              sx={{ mb: 1.5 }}
            />

            <Autocomplete
              multiple
              size="small"
              options={opts.sources}
              value={local.source}
              onChange={(_, v) => set("source", v)}
              renderInput={(p) => <TextField {...p} label="Source" />}
              sx={{ mb: 1.5 }}
            />

            <Autocomplete
              multiple
              size="small"
              options={opts.stages}
              value={local.stage}
              onChange={(_, v) => set("stage", v)}
              renderInput={(p) => <TextField {...p} label="Current Stage" />}
            />
          </Section>

          {/* PEOPLE */}
          <Section title="People & Client">
            <Autocomplete
              size="small"
              options={opts.clients}
              getOptionLabel={(o) => o.business_name || ""}
              value={opts.clients.find((c) => c.id === local.client_id) || null}
              onChange={(_, v) => set("client_id", v ? v.id : "")}
              renderInput={(p) => <TextField {...p} label="Client" />}
              sx={{ mb: 1.5 }}
            />

            <Autocomplete
              size="small"
              options={opts.managers}
              getOptionLabel={(o) => o.name || ""}
              value={opts.managers.find((m) => m.id === local.manager_id) || null}
              onChange={(_, v) => set("manager_id", v ? v.id : "")}
              renderInput={(p) => <TextField {...p} label="Manager" />}
              sx={{ mb: 1.5 }}
            />

            <Autocomplete
              size="small"
              options={opts.assignees}
              getOptionLabel={(o) => (o.name || "") + (o.role ? ` (${o.role})` : "")}
              value={opts.assignees.find((a) => a.id === local.assigned_to) || null}
              onChange={(_, v) => set("assigned_to", v ? v.id : "")}
              renderInput={(p) => <TextField {...p} label="Assigned To" />}
            />
          </Section>

          {/* DATE */}
          <Section title="Created Date Range">
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="date"
                label="From"
                value={local.date_from}
                onChange={(e) => set("date_from", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={local.date_to}
                onChange={(e) => set("date_to", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </Section>

          {/* PRICE */}
          <Section title={`Total Price (₹${opts.price.min} - ₹${opts.price.max})`}>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="number"
                label="Min"
                value={local.price_min}
                onChange={(e) => set("price_min", e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Max"
                value={local.price_max}
                onChange={(e) => set("price_max", e.target.value)}
                fullWidth
              />
            </Stack>
          </Section>

          {/* FLAGS */}
          <Section title="Overdue">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={local.overdue}
              onChange={(_, v) => set("overdue", v === null ? "" : v)}
              fullWidth
            >
              <ToggleButton value="">Any</ToggleButton>
              <ToggleButton value="yes">Overdue</ToggleButton>
              <ToggleButton value="no">On Time</ToggleButton>
            </ToggleButtonGroup>
          </Section>

          {/* SORT */}
          <Section title="Sort">
            <Stack direction="row" spacing={1}>
              <TextField
                select
                size="small"
                label="Sort By"
                value={local.sort_by}
                onChange={(e) => set("sort_by", e.target.value)}
                fullWidth
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Order"
                value={local.sort_order}
                onChange={(e) => set("sort_order", e.target.value)}
                fullWidth
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </TextField>
            </Stack>
          </Section>
        </Box>

        {/* FOOTER */}
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            sx={{ flex: 1 }}
          >
            Reset
          </Button>
          <Button variant="contained" onClick={handleApply} sx={{ flex: 2 }}>
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
